<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.breakpoint.xs"
    max-width="1200"
  >
    <v-card class="preview-dialog">
      <v-card-title class="preview-header">
        <span class="preview-title">Blog Preview</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="onClose" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="preview-content">
        <div v-if="blog" class="blog-preview-container">
          <div class="blog-header">
            <h1 class="blog-title">{{ blog.title }}</h1>
            <div class="meta-info">
              <v-chip small color="primary" class="mr-2">
                <v-icon small left>mdi-calendar</v-icon>
                {{ new Date().toLocaleDateString() }}
              </v-chip>
              <v-chip small>
                <v-avatar left size="24" v-if="profile?.midThumbnail">
                  <v-img
                    :src="profile.midThumbnail"
                    alt="Profile image"
                  ></v-img>
                </v-avatar>
                <v-icon small left v-else>mdi-account</v-icon>
                {{ profile?.name || "Author" }}
              </v-chip>
            </div>
          </div>

          <v-img
            :src="blog.cover"
            alt="Blog cover image"
            class="blog-cover"
            :height="$vuetify.breakpoint.xs ? '200px' : '400px'"
            gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.3)"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="grey lighten-5"
                ></v-progress-circular>
              </v-row>
            </template>
          </v-img>

          <div class="blog-content ql-editor" v-html="blog.html"></div>
        </div>

        <v-skeleton-loader
          v-else
          type="article, image, paragraph@2"
          class="loading-skeleton"
        ></v-skeleton-loader>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="preview-actions">
        <v-btn @click="onClose" color="primary" depressed class="action-btn">
          Close Preview
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.core.css";
import "quill/dist/quill.bubble.css";

export default {
  name: "BlogPreview",
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    blog: Object,
    onClose: Function
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    })
  }
};
</script>

<style scoped lang="scss">
.preview-dialog {
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  .preview-header {
    padding: 16px 24px;
    background-color: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 2;

    .preview-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #2c3e50;
    }

    .close-btn {
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
    }
  }

  .preview-content {
    padding: 0;
    flex: 1;
    overflow-y: auto;

    .blog-preview-container {
      max-width: 800px;
      margin: 0 auto;
      padding: 24px;

      .blog-header {
        margin-bottom: 32px;
        text-align: center;

        .blog-title {
          font-size: 2.5rem;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 16px;
          color: #2c3e50;
        }

        .meta-info {
          display: flex;
          justify-content: center;
          margin-bottom: 24px;
        }
      }

      .blog-cover {
        border-radius: 8px;
        margin-bottom: 32px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .blog-content {
        font-size: 1.1rem;
        line-height: 1.8;
        color: #4a4a4a;

        ::v-deep img {
          max-width: 100%;
          height: auto;
          border-radius: 4px;
        }

        ::v-deep iframe {
          max-width: 100%;
        }
      }
    }

    .loading-skeleton {
      max-width: 800px;
      margin: 0 auto;
      padding: 24px;
    }
  }

  .preview-actions {
    padding: 12px 24px;
    background-color: #f8f9fa;
    position: sticky;
    bottom: 0;
    z-index: 2;
    display: flex;
    justify-content: flex-end;

    .action-btn {
      text-transform: none;
      letter-spacing: normal;
      font-weight: 500;
    }
  }
}

// Mobile styles
@media (max-width: 960px) {
  .preview-dialog {
    .preview-content {
      .blog-preview-container {
        padding: 16px;

        .blog-header {
          .blog-title {
            font-size: 1.8rem;
          }
        }
      }
    }
  }
}

@media (max-width: 600px) {
  .preview-dialog {
    margin: 0;
    border-radius: 0;

    .preview-header {
      padding: 12px 16px;
    }

    .preview-content {
      .blog-preview-container {
        padding: 12px;

        .blog-header {
          .blog-title {
            font-size: 1.5rem;
          }

          .meta-info {
            flex-wrap: wrap;
            gap: 8px;
          }
        }
      }
    }

    .preview-actions {
      padding: 8px 16px;
    }
  }
}

// Quill editor content styles
.ql-editor {
  font-family: inherit;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 1.5em;
    margin-bottom: 0.8em;
    line-height: 1.3;
  }

  p {
    margin-bottom: 1.5em;
  }

  img {
    max-width: 100%;
  }

  blockquote {
    border-left: 4px solid #ddd;
    padding-left: 16px;
    margin-left: 0;
    color: #777;
    font-style: italic;
  }

  pre {
    background-color: #f5f7fa;
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
  }
}
.v-chip.v-size--small {
  border-radius: 20px;
  font-size: 12px;
  height: 34px;
}
</style>
